import React, { useCallback, useEffect, useState } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import Logger from "../../../services/Logger";
import SocialTabs from "../../advertisement/social/SocialTabs";
import FacebookTestService from "../word-wheel-social/FacebookTestService";

const IMAGE_SOURCE_OPTIONS = [
    { value: "random", label: "Random (jigsaw or sliding)" },
    { value: "jigsaw", label: "Jigsaw" },
    { value: "sliding", label: "Sliding" },
];

function isFailureResponse(data) {
    return Boolean(data && data.code === "FAILURE");
}

function imageKindLabel(kind) {
    switch (kind) {
        case "JIGSAW_PREVIEW":
            return "Jigsaw";
        case "SLIDING_PREVIEW":
            return "Sliding";
        case "STORY_ORIGINAL":
            return "Illustration";
        default:
            return kind || "—";
    }
}

export default function TestFacebookMain() {
    const [status, setStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(true);
    const [statusError, setStatusError] = useState(null);

    const [imageSource, setImageSource] = useState("random");
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(true);
    const [previewError, setPreviewError] = useState(null);

    const [caption, setCaption] = useState("");
    const [postLoading, setPostLoading] = useState(false);
    const [postResult, setPostResult] = useState(null);
    const [postError, setPostError] = useState(null);
    const [pool, setPool] = useState([]);
    const [poolIndex, setPoolIndex] = useState(0);

    const loadStatus = useCallback(async () => {
        setStatusLoading(true);
        setStatusError(null);
        try {
            const result = await FacebookTestService.getStatus();
            if (isFailureResponse(result)) {
                setStatus(null);
                setStatusError(result.message || "Failed to load Facebook status");
            } else {
                setStatus(result);
            }
        } catch (err) {
            Logger.error("Facebook status error:", err);
            setStatus(null);
            setStatusError(err.message || "Failed to load Facebook status");
        } finally {
            setStatusLoading(false);
        }
    }, []);

    const showItem = useCallback((item) => {
        if (!item) {
            setPreview(null);
            setCaption("");
            return;
        }
        setPreview(item);
        setCaption(typeof item.caption === "string" ? item.caption : "");
    }, []);

    const loadPool = useCallback(async (source) => {
        setPreviewLoading(true);
        setPreviewError(null);
        setPostResult(null);
        setPostError(null);
        try {
            const result = await FacebookTestService.getCandidates({ imageSource: source, limit: 24 });
            if (isFailureResponse(result)) {
                setPool([]);
                setPoolIndex(0);
                showItem(null);
                setPreviewError(result.message || "Failed to load candidates");
                return;
            }
            const items = Array.isArray(result?.items)
                ? result.items.filter((it) => it && typeof it.imageUrl === "string" && it.imageUrl.trim())
                : [];
            if (items.length === 0) {
                setPool([]);
                setPoolIndex(0);
                showItem(null);
                setPreviewError(result?.reason || "No published standalone jigsaw/sliding image found");
                return;
            }
            setPool(items);
            setPoolIndex(0);
            showItem(items[0]);
        } catch (err) {
            Logger.error("Facebook candidates error:", err);
            try {
                const fallback = await FacebookTestService.nextCandidate({ imageSource: source });
                if (fallback?.found) {
                    setPool([fallback]);
                    setPoolIndex(0);
                    showItem(fallback);
                    return;
                }
            } catch (fallbackErr) {
                Logger.error("Facebook next-candidate fallback error:", fallbackErr);
            }
            setPool([]);
            setPoolIndex(0);
            showItem(null);
            setPreviewError(err.message || "Failed to load candidates");
        } finally {
            setPreviewLoading(false);
        }
    }, [showItem]);

    const handleGetCandidate = async () => {
        setPostResult(null);
        setPostError(null);
        if (pool.length > 1) {
            const next = (poolIndex + 1) % pool.length;
            setPoolIndex(next);
            showItem(pool[next]);
            return;
        }
        setPreviewLoading(true);
        setPreviewError(null);
        try {
            const result = await FacebookTestService.nextCandidate({
                imageSource,
                excludeStoryId: preview?.storyId,
            });
            if (isFailureResponse(result) || !result?.found) {
                setPreviewError(result?.message || result?.reason || "No next candidate");
                return;
            }
            if (preview?.storyId && result.storyId === preview.storyId) {
                setPreviewError("Only one unpublished pack is available.");
            }
            setPool([result]);
            setPoolIndex(0);
            showItem(result);
        } catch (err) {
            Logger.error("Facebook next-candidate error:", err);
            setPreviewError(err.message || "Failed to load next candidate");
        } finally {
            setPreviewLoading(false);
        }
    };

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    useEffect(() => {
        loadPool(imageSource);
    }, [imageSource, loadPool]);

    const handlePost = async () => {
        const url = preview?.imageUrl?.trim();
        if (!url) {
            setPostError("No target image to post.");
            return;
        }
        if (!url.startsWith("https://")) {
            setPostError("Image URL must be public HTTPS so Facebook can fetch it.");
            return;
        }

        setPostLoading(true);
        setPostError(null);
        setPostResult(null);
        try {
            const result = await FacebookTestService.postImageUrl(url, caption, { published: true });
            setPostResult(result);
        } catch (err) {
            Logger.error("Facebook post-url error:", err);
            setPostError(err.message || "Failed to post image");
        } finally {
            setPostLoading(false);
        }
    };

    const adminReady = Boolean(status?.adminUploadReady);

    return (
        <Box sx={{ maxWidth: 960, mx: "auto", p: { xs: 1, sm: 2 } }}>
            <SocialTabs />

            <Typography variant="h5" gutterBottom>
                Test Facebook
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Get a candidate standalone image, then post it as a public Page photo.
                Each Get candidate click loads the next unpublished pack.
            </Typography>

            <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                        <Typography variant="h6">Connection</Typography>
                        <Button
                            size="small"
                            startIcon={statusLoading ? <CircularProgress size={14} /> : <RefreshIcon />}
                            onClick={loadStatus}
                            disabled={statusLoading}
                        >
                            Refresh
                        </Button>
                    </Box>
                    {statusError ? <Alert severity="error" sx={{ mb: 2 }}>{statusError}</Alert> : null}
                    {status ? (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            <Chip
                                label={status.adminUploadReady ? "Ready to post" : "Not ready"}
                                color={status.adminUploadReady ? "success" : "warning"}
                                size="small"
                            />
                            <Chip
                                label={status.hasPageAccessToken ? "Page token" : "No page token"}
                                color={status.hasPageAccessToken ? "success" : "default"}
                                size="small"
                            />
                            {status.pageId ? (
                                <Chip label={`Page ${status.pageId}`} size="small" variant="outlined" />
                            ) : null}
                        </Box>
                    ) : null}
                    {status?.readinessGap && !status.adminUploadReady ? (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            {status.readinessGap}
                        </Alert>
                    ) : null}
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardContent>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            flexWrap: "wrap",
                            mb: 2,
                        }}
                    >
                        <Typography variant="h6">Target image</Typography>
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel id="fb-test-image-source">Puzzle</InputLabel>
                                <Select
                                    labelId="fb-test-image-source"
                                    label="Puzzle"
                                    value={imageSource}
                                    onChange={(e) => setImageSource(e.target.value)}
                                    disabled={previewLoading || postLoading}
                                >
                                    {IMAGE_SOURCE_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Button
                                variant="contained"
                                startIcon={previewLoading ? <CircularProgress size={16} /> : <ShuffleIcon />}
                                onClick={handleGetCandidate}
                                disabled={previewLoading || postLoading}
                                sx={{ minHeight: 44 }}
                            >
                                Get candidate
                            </Button>
                        </Box>
                    </Box>

                    {previewError ? <Alert severity="error" sx={{ mb: 2 }}>{previewError}</Alert> : null}

                    {previewLoading && !preview ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2" color="text.secondary">
                                Loading target puzzle…
                            </Typography>
                        </Box>
                    ) : null}

                    {preview ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                <Chip label={imageKindLabel(preview.imageKind)} color="primary" size="small" />
                                <Chip
                                    label={preview.fromBacklog ? "Scheduler backlog" : "Newest published pack"}
                                    size="small"
                                    variant="outlined"
                                />
                                {pool.length > 0 ? (
                                    <Chip
                                        label={`${poolIndex + 1} of ${pool.length}`}
                                        size="small"
                                        variant="outlined"
                                    />
                                ) : preview.candidateIndex != null && preview.candidateTotal != null ? (
                                    <Chip
                                        label={`${preview.candidateIndex} of ${preview.candidateTotal}`}
                                        size="small"
                                        variant="outlined"
                                    />
                                ) : null}
                                {preview.facebookPublishedAt ? (
                                    <Chip label="Already marked posted" size="small" color="warning" />
                                ) : null}
                            </Box>
                            {preview.title ? (
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {preview.title}
                                </Typography>
                            ) : null}
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "ui-monospace, monospace" }}>
                                {preview.storyId}
                            </Typography>

                            <Box
                                sx={{
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    p: 1,
                                    maxWidth: 480,
                                    bgcolor: "background.paper",
                                }}
                            >
                                <Box
                                    component="img"
                                    key={`${preview.storyId}-${preview.imageUrl}`}
                                    src={preview.imageUrl}
                                    alt={preview.title || "Target puzzle"}
                                    sx={{
                                        display: "block",
                                        maxWidth: "100%",
                                        maxHeight: 420,
                                        objectFit: "contain",
                                    }}
                                />
                            </Box>

                            <TextField
                                label="Caption"
                                value={caption}
                                onChange={(e) => setCaption(e.target.value)}
                                fullWidth
                                multiline
                                minRows={3}
                                size="small"
                                disabled={postLoading}
                            />

                            <Box>
                                <Button
                                    variant="contained"
                                    onClick={handlePost}
                                    disabled={postLoading || !adminReady || !preview.imageUrl}
                                >
                                    {postLoading ? "Posting…" : "Post"}
                                </Button>
                                {!adminReady && status && !statusLoading ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
                                        Page id and access token are required before posting.
                                    </Typography>
                                ) : null}
                            </Box>
                        </Box>
                    ) : null}

                    {postError ? <Alert severity="error" sx={{ mt: 2 }}>{postError}</Alert> : null}
                    {postResult ? (
                        <Alert severity="success" sx={{ mt: 2 }}>
                            Posted — {postResult.audience === "public_timeline" ? "public Page photo" : "unpublished"}
                            {postResult.postId ? ` · post ${postResult.postId}` : ""}
                            {postResult.photoId ? ` · photo ${postResult.photoId}` : ""}
                        </Alert>
                    ) : null}
                </CardContent>
            </Card>
        </Box>
    );
}
